'use client';

import { useEffect, useRef, useState } from 'react';
import { useT } from '@/components/i18n-provider';
import { useRouter } from 'next/navigation';
import { Bug, Check, ChevronDown, Pencil, Plus, Share2, Trash2 } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useAuth } from '@/components/auth-provider';
import { openPlansDialog } from '@/components/plans-dialog-host';
import { ShareDialog } from '@/components/sharing/share-dialog';
import { useWorkspace, useWorkspaceActions } from '@/components/workspace/store';
import {
  uniqueParticipants,
  useParticipantsMirror,
} from '@/components/workspace/presence/presence-bridge';
import { useWorkspaceCardActions } from '@/components/workspace/card-actions-context';
import { MyFilesSummary } from '@/components/workspace/my-files-summary';
import { openDiscover } from '@/components/workspace/discover/discover-events';
import { useStarters } from '@/components/workspace/discover/starter-band';
import { emitWorkspaceListChanged } from '@/lib/workspaces/list-events';
import { useCoarsePointer } from '@/hooks/use-coarse-pointer';

/**
 * Stage's four corners. Stage is the meta layer — the one place you are not
 * working — so it holds controls ABOUT the workspace rather than in it, on a
 * legible rule: the LEFT column is yours (this workspace above, your whole
 * library below), the RIGHT column is everyone else (who is here above, help
 * below). Mounted by the canvas only in overview mode; everything here is
 * relocation of existing capability, not new capability.
 *
 * Two of the corners open something that lives on a screen or in Discover,
 * and Stage spans every screen — so those actions EXIT Stage first (a mode
 * change the user expected beats a window appearing on a screen they cannot
 * identify).
 */

type WsRoot = { id: string; name: string; owned?: boolean };
/**
 * The person's workspace cap as `GET /api/users/me/workspaces` reports it
 * (the same numbers `POST /api/workspaces` refuses on). `ownedCount` is what
 * the cap counts (workspaces the person created), NOT the rows flagged
 * `owned` below, which also count their agents' and guests' workspaces.
 */
type WsCap = { ownedCount: number; limit: number; canUpgrade: boolean };

const exitStage = () =>
  window.dispatchEvent(
    new CustomEvent('ws:screen', { detail: { kind: 'overview-toggle' } }),
  );

export function StageCorners() {
  const t = useT();
  const workspace = useWorkspace();
  const { user } = useAuth();
  const router = useRouter();
  // One dot per PERSON, not per connection (a user's second tab is not a
  // second collaborator).
  const participants = uniqueParticipants(useParticipantsMirror());
  const cardActions = useWorkspaceCardActions();
  const starters = useStarters();

  // ---- top left: where am I (workspace switcher) --------------------------
  const [switcherOpen, setSwitcherOpen] = useState(false);
  const [workspaces, setWorkspaces] = useState<WsRoot[] | null>(null);
  // The cap rides the same answer. Until the e2e run of 2026-09-26 the "New
  // workspace" row acted only on `res.ok`, so a Free account at its cap
  // pressed it and nothing happened while every other creator opened the
  // Plans dialog on the same 403. The row now knows the cap BEFORE the click
  // (disabled, "5 of 5 workspaces", Upgrade) and a 403 lands on the same line.
  const [cap, setCap] = useState<WsCap | null>(null);
  const switcherRef = useRef<HTMLDivElement | null>(null);
  const loadWorkspaces = () =>
    fetch('/api/users/me/workspaces', { credentials: 'same-origin' })
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => {
        setWorkspaces((d?.workspaces ?? []) as WsRoot[]);
        if (typeof d?.limit === 'number' && typeof d?.ownedCount === 'number') {
          setCap({ ownedCount: d.ownedCount, limit: d.limit, canUpgrade: d.canUpgrade !== false });
        }
      })
      .catch(() => setWorkspaces([]));
  useEffect(() => {
    if (!switcherOpen || workspaces !== null) return;
    void loadWorkspaces();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [switcherOpen, workspaces]);
  const atCap = !!cap && cap.ownedCount >= cap.limit;
  useEffect(() => {
    if (!switcherOpen) return;
    const onDown = (e: PointerEvent) => {
      if (!switcherRef.current?.contains(e.target as Node)) setSwitcherOpen(false);
    };
    window.addEventListener('pointerdown', onDown);
    return () => window.removeEventListener('pointerdown', onDown);
  }, [switcherOpen]);
  // Inline manage (user call: the workspaces PANEL is retired — Stage is
  // the place you manage the workspace, so rename/delete live right on the
  // switcher rows). Hover shows the verbs; rename swaps the row for an
  // input; delete swaps it for a one-line confirm. Same endpoints as the
  // old panel: store rename for the CURRENT workspace (every useWorkspace
  // reader updates), PATCH for others, DELETE + navigate-away for current.
  //
  // The verbs were hover-ONLY until the e2e run of 2026-09-26, which found
  // no way to rename from a keyboard or a phone ("no Rename in the
  // switcher"): they now also show while a row has focus and always on a
  // coarse pointer, and the footer carries a plain Rename for the current
  // workspace so the verb exists somewhere a person can read it.
  const isCoarse = useCoarsePointer();
  const { renameWorkspace } = useWorkspaceActions();
  const [wsRenamingId, setWsRenamingId] = useState<string | null>(null);
  const [wsRenameDraft, setWsRenameDraft] = useState('');
  const [wsConfirmDeleteId, setWsConfirmDeleteId] = useState<string | null>(null);
  useEffect(() => {
    // Reset transient row states whenever the dropdown closes.
    if (!switcherOpen) { setWsRenamingId(null); setWsConfirmDeleteId(null); }
  }, [switcherOpen]);

  const commitWsRename = async (ws: WsRoot) => {
    const next = wsRenameDraft.trim();
    setWsRenamingId(null);
    if (!next || next === ws.name) return;
    try {
      if (ws.id === workspace.id) {
        await renameWorkspace(next);
      } else {
        await fetch(`/api/workspaces/${ws.id}`, {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ name: next }),
        });
      }
      setWorkspaces((prev) =>
        prev ? prev.map((w) => (w.id === ws.id ? { ...w, name: next } : w)) : prev,
      );
      emitWorkspaceListChanged();
      router.refresh();
    } catch {
      /* refresh reconciles */
    }
  };

  const deleteWs = async (ws: WsRoot) => {
    setWsConfirmDeleteId(null);
    try {
      const res = await fetch(`/api/workspaces/${ws.id}`, { method: 'DELETE' });
      if (res.ok) {
        setWorkspaces((prev) => (prev ? prev.filter((w) => w.id !== ws.id) : prev));
        emitWorkspaceListChanged();
        if (ws.id === workspace.id) router.push('/workspace');
        // The cap moved (a row the person created is gone): re-read it rather
        // than guess, since `owned` is wider than what the cap counts.
        else void loadWorkspaces();
      }
    } catch {
      /* ignore */
    }
  };

  const createWorkspace = async () => {
    try {
      const res = await fetch('/api/workspaces', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: 'Untitled workspace' }),
      });
      if (res.ok) {
        const data = await res.json();
        emitWorkspaceListChanged();
        if (data?.workspace?.id) router.push(`/workspace/${data.workspace.id}`);
        return;
      }
      if (res.status === 403) {
        // The per-plan cap. The 403 body carries `limit` (and `ownedCount`),
        // so the row turns into the same "N of N workspaces" line the
        // pre-click state draws, with Upgrade when a bigger plan exists
        // (`upgrade_required`; Premium gets `workspace_limit` and no button).
        const j = await res.clone().json().catch(() => null);
        if (typeof j?.limit === 'number') {
          setCap({
            ownedCount: typeof j.ownedCount === 'number' ? j.ownedCount : j.limit,
            limit: j.limit,
            canUpgrade: j.code === 'upgrade_required',
          });
        }
      }
    } catch {
      // The row simply does not appear; the switcher stays usable.
    }
  };

  // ---- top right: who is here (share + presence) ---------------------------
  const [shareOpen, setShareOpen] = useState(false);

  // ---- bottom left: your library (My files + quota) ------------------------
  const [storage, setStorage] = useState<{ used: number; quota: number } | null>(null);
  useEffect(() => {
    if (!user) return;
    const c = new AbortController();
    fetch('/api/user', { signal: c.signal })
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => {
        if (c.signal.aborted || !d?.storageQuota) return;
        setStorage({ used: d.storageUsed ?? 0, quota: d.storageQuota });
      })
      .catch(() => {});
    return () => c.abort();
  }, [user]);

  const openMyFiles = () => {
    exitStage();
    openDiscover({ kind: 'files' });
  };

  // ---- bottom right: help (report a problem) -------------------------------
  const reportProblem = () => {
    const feedback = starters?.feedback;
    if (!feedback || !cardActions) return;
    // Exit Stage FIRST, then spawn on the screen the camera returns to — the
    // per-screen placement centers it there at the widget's DECLARED size.
    // No layout event: a feedback form filling the whole screen read as a
    // takeover, not help.
    exitStage();
    void cardActions.addWidget({
      contentId: feedback.contentId,
      title: feedback.title,
      metadata: feedback.metadata,
      sourceWidgetPostId: feedback.postId,
    });
  };

  const chip =
    'pointer-events-auto inline-flex items-center gap-1.5 rounded-lg border border-border/60 bg-background px-2.5 py-1.5 text-xs font-medium text-foreground shadow-sm transition-colors hover:bg-accent';
  const untitled = t('workspace.canvas.untitled');
  // The current workspace's own row, when the list carries it and it is
  // ours: the footer Rename acts on this row (the input swaps in above).
  const currentRow = (workspaces ?? []).find((w) => w.id === workspace.id);
  const canRenameCurrent = !!currentRow && currentRow.owned !== false;

  return (
    <div className="pointer-events-none absolute inset-0 z-40">
      {/* Top left — where am I. */}
      {user && (
        <div ref={switcherRef} className="absolute left-3 top-3">
          <button
            type="button"
            onClick={() => setSwitcherOpen((v) => !v)}
            className={chip}
            data-testid="stage-workspace-switcher"
          >
            <span className="max-w-[16rem] truncate">{workspace.name || 'Workspace'}</span>
            <ChevronDown className="h-3.5 w-3.5 text-muted-foreground" />
          </button>
          {switcherOpen && (
            <div className="pointer-events-auto absolute left-0 top-full z-50 mt-1 w-64 overflow-hidden rounded-lg border border-border bg-popover shadow-lg">
              <div className="max-h-72 overflow-y-auto p-1">
                {(workspaces ?? []).map((ws) =>
                  wsRenamingId === ws.id ? (
                    <input
                      key={ws.id}
                      autoFocus
                      value={wsRenameDraft}
                      onChange={(e) => setWsRenameDraft(e.target.value)}
                      onBlur={() => void commitWsRename(ws)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') void commitWsRename(ws);
                        if (e.key === 'Escape') setWsRenamingId(null);
                      }}
                      className="w-full rounded-md border border-primary/50 bg-background px-2 py-1.5 text-xs text-foreground outline-none"
                    />
                  ) : wsConfirmDeleteId === ws.id ? (
                    <div
                      key={ws.id}
                      className="flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-xs"
                    >
                      <span className="min-w-0 flex-1 truncate text-muted-foreground">
                        {t('stage.deleteWorkspaceConfirm', { name: ws.name || untitled })}
                      </span>
                      <button
                        type="button"
                        onClick={() => void deleteWs(ws)}
                        className="shrink-0 rounded-md bg-red-500/10 px-2 py-0.5 font-medium text-red-500 hover:bg-red-500/20"
                      >
                        {t('actions.delete')}
                      </button>
                      <button
                        type="button"
                        onClick={() => setWsConfirmDeleteId(null)}
                        className="shrink-0 rounded-md px-1.5 py-0.5 text-muted-foreground hover:bg-accent/60 hover:text-foreground"
                      >
                        {t('actions.cancel')}
                      </button>
                    </div>
                  ) : (
                    <div
                      key={ws.id}
                      className="group/wsrow flex w-full items-center gap-1 rounded-md pr-1 transition-colors hover:bg-accent/60"
                    >
                      <button
                        type="button"
                        onClick={() => {
                          setSwitcherOpen(false);
                          if (ws.id !== workspace.id) router.push(`/workspace/${ws.id}`);
                        }}
                        className="flex min-w-0 flex-1 items-center gap-2 px-2 py-1.5 text-left text-xs text-foreground"
                      >
                        {/* LEADING check in a slot every row reserves — a
                            trailing check shifted left whenever the hover
                            verbs appeared beside it (the "jumping" mark). */}
                        <span className="flex h-3.5 w-3.5 shrink-0 items-center justify-center">
                          {ws.id === workspace.id && (
                            <Check className="h-3.5 w-3.5 text-muted-foreground" />
                          )}
                        </span>
                        <span className="min-w-0 flex-1 truncate">{ws.name || untitled}</span>
                      </button>
                      {/* Row verbs — rename swaps the row for an input,
                          delete for a one-line confirm. Owned rows only.
                          Shown on hover, while anything in the row has
                          focus (Tab reaches them), and always on a coarse
                          pointer (a phone has no hover). */}
                      {ws.owned !== false && (
                        <span
                          className={cn(
                            'shrink-0 items-center gap-0.5',
                            isCoarse
                              ? 'flex'
                              : 'hidden group-hover/wsrow:flex group-focus-within/wsrow:flex',
                          )}
                        >
                          <button
                            type="button"
                            aria-label={t('stage.renameWorkspace', { name: ws.name || untitled })}
                            onClick={() => {
                              setWsRenameDraft(ws.name);
                              setWsRenamingId(ws.id);
                            }}
                            className="rounded p-1 text-muted-foreground hover:bg-accent hover:text-foreground"
                          >
                            <Pencil className="h-3 w-3" />
                          </button>
                          <button
                            type="button"
                            aria-label={t('stage.deleteWorkspace', { name: ws.name || untitled })}
                            onClick={() => setWsConfirmDeleteId(ws.id)}
                            className="rounded p-1 text-muted-foreground hover:bg-red-500/10 hover:text-red-500"
                          >
                            <Trash2 className="h-3 w-3" />
                          </button>
                        </span>
                      )}
                    </div>
                  ),
                )}
                {workspaces !== null && workspaces.length === 0 && (
                  <p className="px-2 py-2 text-xs text-muted-foreground">{t('stage.noOtherWorkspaces')}</p>
                )}
              </div>
              {/* Rename THIS workspace: the one verb the e2e run could not
                  find. Swaps the current row above for its input. */}
              {canRenameCurrent && currentRow && wsRenamingId !== currentRow.id && (
                <button
                  type="button"
                  onClick={() => {
                    setWsRenameDraft(currentRow.name);
                    setWsRenamingId(currentRow.id);
                  }}
                  className="flex w-full items-center gap-2 border-t border-border/60 px-3 py-2 text-left text-xs font-medium text-foreground transition-colors hover:bg-accent/60"
                  data-testid="stage-rename-workspace"
                >
                  <Pencil className="h-3.5 w-3.5" />
                  {t('workspace.canvas.rename')}
                </button>
              )}
              {atCap && cap ? (
                <div
                  className="flex w-full items-center gap-2 border-t border-border/60 px-3 py-2 text-xs"
                  data-testid="stage-workspace-cap"
                >
                  <span
                    aria-disabled="true"
                    className="flex min-w-0 flex-1 items-center gap-2 text-muted-foreground"
                  >
                    <Plus className="h-3.5 w-3.5 shrink-0 opacity-50" />
                    <span className="truncate">
                      {t('stage.workspaceCap', { count: cap.ownedCount, limit: cap.limit })}
                    </span>
                  </span>
                  {cap.canUpgrade && (
                    <button
                      type="button"
                      onClick={() => {
                        setSwitcherOpen(false);
                        openPlansDialog();
                      }}
                      className="shrink-0 rounded-md bg-primary px-2 py-0.5 font-medium text-primary-foreground transition-colors hover:bg-primary/90"
                      data-testid="stage-workspace-upgrade"
                    >
                      {t('stage.upgrade')}
                    </button>
                  )}
                </div>
              ) : (
                <button
                  type="button"
                  onClick={() => void createWorkspace()}
                  className="flex w-full items-center gap-2 border-t border-border/60 px-3 py-2 text-left text-xs font-medium text-foreground transition-colors hover:bg-accent/60"
                  data-testid="stage-new-workspace"
                >
                  <Plus className="h-3.5 w-3.5" />
                  {t('workspace.canvas.newWorkspace')}
                </button>
              )}

            </div>
          )}
        </div>
      )}

      {/* Top right — who is here. */}
      {user && (
        <div className="absolute right-3 top-3 flex items-center gap-2">
          {participants.length > 0 && (
            <div className="pointer-events-auto flex items-center -space-x-1">
              {participants.slice(0, 5).map((p) => (
                <span
                  key={p.user.peerId}
                  title={p.user.username}
                  className="inline-block h-2.5 w-2.5 rounded-full ring-2 ring-background"
                  style={{ background: p.user.color }}
                />
              ))}
            </div>
          )}
          <button
            type="button"
            onClick={() => setShareOpen(true)}
            className={chip}
            data-testid="stage-share"
          >
            <Share2 className="h-3.5 w-3.5" />
            Share
          </button>
        </div>
      )}

      {/* Bottom left — your library. Hidden below md: the bottom band
          belongs to ScreenPageDots there, and My files stays reachable
          through Discover. */}
      {user && (
        <button
          type="button"
          onClick={openMyFiles}
          className={cn(chip, 'absolute bottom-3 left-3 hidden md:inline-flex')}
          data-testid="stage-my-files"
        >
          {/* Same face as Discover's pinned footer (MyFilesSummary) — the
              affordance must read as one object on both surfaces. */}
          <MyFilesSummary storage={storage} />
        </button>
      )}

      {/* Bottom right — help. Hidden below md with its bottom-left sibling. */}
      {starters?.feedback && cardActions && (
        <button
          type="button"
          onClick={reportProblem}
          className={cn(chip, 'absolute bottom-3 right-3 hidden md:inline-flex')}
          data-testid="stage-report-problem"
        >
          <Bug className="h-3.5 w-3.5 text-muted-foreground" />
          {t('stage.reportProblem')}
        </button>
      )}

      {shareOpen && (
        <div className="pointer-events-auto">
          <ShareDialog
            kind="workspace"
            workspaceId={workspace.id}
            onClose={() => setShareOpen(false)}
          />
        </div>
      )}
    </div>
  );
}
